import { useMutation } from '@tanstack/react-query';

import TQConnectUserAPI from '../../../api/TQConnectUserAPI';
import { updateUserDetails } from '../../../context/userReducer';
import showAppError from '../../../shared/error';
import { useAppDispatch } from '../../../shared/hooks';
import { queryClient } from '../../../shared/queryClient';
import { QUERY_KEYS } from '../../../shared/queryKeys';
import {
  ICreateTQShoutoutRequest,
  ITQProfileCompletionResponseRequest,
} from '../../../types/tqConnectTypes/user';

/**
 * Custom hook to submit TQ Connect profile query response
 *
 * @param userId - The user ID to submit profile query response for
 * @returns useMutation result for submitting profile query response
 */
export const useSubmitTQProfileCompletionQuery = (userId: string) => {
  const dispatch = useAppDispatch();

  const { mutate: submitTQProfileCompletionQuery, ...values } = useMutation({
    mutationFn: (data: ITQProfileCompletionResponseRequest) => {
      return TQConnectUserAPI.submitProfileQueryResponse(data);
    },
    onSettled: (resp, error) => {
      if (resp?.data?.code === 200) {
        // Invalidate profile completion queries to refetch updated data
        queryClient.invalidateQueries({
          queryKey: QUERY_KEYS.TQ_CONNECT_USER_PROFILE_INFO(userId),
        });
        dispatch(
          updateUserDetails({
            tqConnectScore: resp.data.result.assignedTQScore,
            tqConnectProfileCompletionResponses:
              resp.data.result.tqConnectProfileCompletionResponses,
          }),
        );
      } else {
        showAppError(error);
      }
    },
  });

  return { submitTQProfileCompletionQuery, ...values };
};

/**
 * Custom hook to create TQ Connect profile shoutout
 *
 * @returns useMutation result for creating profile shoutout
 */
export const useCreateTQProfileShoutout = () => {
  const { mutate: createTQProfileShoutout, ...values } = useMutation({
    mutationFn: (data: ICreateTQShoutoutRequest) => {
      return TQConnectUserAPI.createProfileShoutout(data);
    },
    onSettled: (resp, error) => {
      if (resp?.data?.code === 200) {
        // Invalidate all shoutout queries to refetch updated data
        queryClient.invalidateQueries({
          predicate: (query) =>
            query.queryKey[0] === 'TQ_CONNECT_PROFILE_SHOUTOUTS',
        });
      } else {
        showAppError(error);
      }
    },
  });

  return { createTQProfileShoutout, ...values };
};
